import {Logger} from 'src/app/core/interfaces/Logger';
import {StoragePort} from '@platform/storage/domain/ports/storage.port';
import {IndexedDBRepository} from '@shared/interface/indexed-db-storage.model';
import {catchError, map, Observable, of, OperatorFunction} from 'rxjs';

const DB_NAME = 'fv_pos_db';
const STORAGE_NAME = 'state';
const LOG_CONTEXT = '[StoreIndexedDbStorage]';

export class StoreIndexedDbStorage<T = unknown> extends IndexedDBRepository<T> implements StoragePort<T> {
  protected readonly dbName = DB_NAME;

  constructor(
    private readonly logger: Logger,
    protected readonly instanceName: string,
  ) {
    super();
  }

  get(): Observable<T | null> {
    return this.getItem(STORAGE_NAME).pipe(
      map((value) => (value ?? null) as T | null),
      this.handleError('get', null),
    );
  }

  set(value: T | null): Observable<T | null> {
    return value == null
      ? this.remove().pipe(map(() => null))
      : this.setItem(STORAGE_NAME, value).pipe(map(() => value), this.handleError('set', null),
      );
  }

  remove(): Observable<void> {
    return this.removeItem(STORAGE_NAME).pipe(
      this.handleError('remove', void 0),
    );
  }

  clear(): Observable<void> {
    return this.clearAll().pipe(
      this.handleError('clear', void 0),
    );
  }

  private handleError<T, R>(operation: string, fallback: R): OperatorFunction<T, T | R> {
    return catchError((error) => {
      this.logger.error(error, `${LOG_CONTEXT} ${operation}("${STORAGE_NAME}") failed`);

      return of(fallback);
    });
  }
}
